import { createFileRoute, redirect, retainSearchParams } from "@tanstack/react-router";
import { useState } from "react";
import { ConfigCodeView } from "../features/customization/components/config-code-overlay";
import type { WidgetConfig } from "../features/customization/lib/config-schema";
import { normalizeFeatures } from "../features/customization/lib/config-schema";
import { DevTools } from "../shared/components/dev-tools";
import { Shell } from "../shared/components/shell";
import { ViewToggle } from "../shared/components/view-toggle";
import { Widget } from "../shared/components/widget";
import { queryClient } from "../shared/lib/query-client";
import { useEmbedMode } from "../shared/utils/embed-detection";
import { getDefaultTab, TAB_TO_ROUTE, type TabName } from "../shared/utils/navigation-utils";

export const Route = createFileRoute("/_layout")({
  component: LayoutComponent,
  search: {
    middlewares: [retainSearchParams(true)],
  },
  beforeLoad: ({ location }) => {
    // Get config from query cache (should already be loaded by WidgetConfigProvider)
    const config = queryClient.getQueryData<WidgetConfig>(["config"]);

    if (!config) {
      // Config not loaded yet, let the route continue (will be handled by provider)
      return;
    }

    // Normalize features to get boolean values
    const features = normalizeFeatures(config.features);

    // Determine which feature the current route belongs to
    const getFeatureFromPath = (pathname: string): TabName | null => {
      if (pathname === "/" || pathname.startsWith("/portfolio")) return "Portfolio";
      if (pathname.startsWith("/rewards")) return "Rewards";
      if (pathname.startsWith("/defi")) return "DeFi";
      if (pathname.startsWith("/swap")) return "Swap";
      if (pathname.startsWith("/discover")) return "Discover";
      return null;
    };

    const currentTab = getFeatureFromPath(location.pathname);

    // If on root path, always redirect to first enabled feature
    if (location.pathname === "/") {
      const defaultTab = getDefaultTab(features);
      const redirectRoute = TAB_TO_ROUTE[defaultTab];
      throw redirect({ to: redirectRoute });
    }

    // If we're on a feature route and it's disabled, redirect to first enabled feature
    if (currentTab) {
      const featureKey = currentTab.toLowerCase() as keyof typeof features;

      if (!features[featureKey]) {
        const defaultTab = getDefaultTab(features);
        const redirectRoute = TAB_TO_ROUTE[defaultTab];
        throw redirect({ to: redirectRoute });
      }
    }
  },
});

const isWidgetOnlyBuild = import.meta.env.VITE_WIDGET_ONLY === "true";

function LayoutComponent() {
  const [showCode, setShowCode] = useState(false);
  const embed = useEmbedMode();

  const handleToggleCode = () => {
    setShowCode((prev) => !prev);
  };

  // Widget-only build or embed mode: always render widget (no demo components)
  if (isWidgetOnlyBuild || embed) {
    return <Widget />;
  }

  // Full demo app: render with Shell and demo components
  return (
    <Shell>
      {showCode ? <ConfigCodeView /> : <Widget />}
      <ViewToggle showCode={showCode} onToggleCode={handleToggleCode} />
      <DevTools />
    </Shell>
  );
}
